import { useState } from 'react';
import { cn } from '../../../lib/utils';
import type { ReportingMetricAvailability } from '../types';

interface ReportingCoverageBannerProps {
  availability: ReportingMetricAvailability[];
  isLoading?: boolean;
}

export function ReportingCoverageBanner({
  availability,
  isLoading = false,
}: ReportingCoverageBannerProps) {
  const [expanded, setExpanded] = useState(false);

  if (isLoading) {
    return (
      <div
        role="status"
        aria-label="Loading data coverage"
        aria-busy="true"
        className="rounded-lg border border-neutral-800/70 bg-neutral-900/30"
      >
        <div className="flex w-full items-center gap-3 px-4 py-2.5">
          <span className="size-[22px] shrink-0 rounded-full border-2 border-neutral-800 border-t-teal-400 motion-safe:animate-spin" />
          <div className="flex flex-1 flex-wrap items-center gap-x-4 gap-y-1 text-sm">
            <span className="text-neutral-300">Data coverage</span>
            <span className="text-xs text-neutral-500">Loading coverage…</span>
          </div>
        </div>
      </div>
    );
  }

  if (availability.length === 0) {
    return null;
  }

  const available = availability.filter((i) => i.status === 'available').length;
  const inferred = availability.filter((i) => i.status === 'inferred').length;
  const missing = availability.filter((i) => i.status === 'missing').length;
  const total = availability.length;
  const liveRatio = total === 0 ? 0 : available / total;

  return (
    <div className="rounded-lg border border-neutral-800/70 bg-neutral-900/30">
      <button
        type="button"
        onClick={() => setExpanded(!expanded)}
        aria-expanded={expanded}
        className="flex w-full items-center gap-3 px-4 py-2.5 text-left transition-colors hover:bg-white/[0.015]"
      >
        <CoverageGauge ratio={liveRatio} />
        <div className="flex flex-1 flex-wrap items-center gap-x-4 gap-y-1 text-sm">
          <span className="text-neutral-300">Data coverage</span>
          <span className="flex items-center gap-1.5 text-xs text-neutral-500">
            <span className="size-1.5 rounded-full bg-teal-400" />
            <span className="tabular-nums text-neutral-300">
              {available}
            </span>{' '}
            live
          </span>
          <span className="flex items-center gap-1.5 text-xs text-neutral-500">
            <span className="size-1.5 rounded-full bg-neutral-500" />
            <span className="tabular-nums text-neutral-300">
              {inferred}
            </span>{' '}
            inferred
          </span>
          <span className="flex items-center gap-1.5 text-xs text-neutral-500">
            <span className="size-1.5 rounded-full bg-amber-400" />
            <span className="tabular-nums text-neutral-300">
              {missing}
            </span>{' '}
            need source
          </span>
        </div>
        <span className="text-xs text-neutral-500">
          {expanded ? 'Hide' : 'Inspect'}
        </span>
      </button>

      {expanded && (
        <div className="border-t border-neutral-800/70 px-4 py-3">
          <div className="grid gap-x-6 gap-y-1 sm:grid-cols-2 lg:grid-cols-3">
            {availability.map((item) => (
              <div
                key={item.key}
                className="flex items-center justify-between gap-3 py-1 text-xs"
              >
                <span className="text-neutral-400">{item.label}</span>
                <span
                  className={cn(
                    'rounded border px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wider',
                    item.status === 'available'
                      ? 'border-teal-500/20 bg-teal-500/[0.08] text-teal-300'
                      : item.status === 'inferred'
                        ? 'border-neutral-700/60 bg-neutral-800/50 text-neutral-300'
                        : 'border-amber-500/20 bg-amber-500/[0.08] text-amber-300',
                  )}
                >
                  {item.status}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function CoverageGauge({ ratio }: { ratio: number }) {
  const safeRatio = Math.max(0, Math.min(1, ratio));
  const radius = 9;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference * (1 - safeRatio);

  return (
    <svg
      width="22"
      height="22"
      viewBox="0 0 24 24"
      className="shrink-0"
      aria-hidden="true"
    >
      <circle
        cx="12"
        cy="12"
        r={radius}
        fill="none"
        stroke="rgb(38 38 38)"
        strokeWidth="2.5"
      />
      <circle
        cx="12"
        cy="12"
        r={radius}
        fill="none"
        stroke="rgb(45 212 191)"
        strokeWidth="2.5"
        strokeDasharray={circumference}
        strokeDashoffset={offset}
        strokeLinecap="round"
        transform="rotate(-90 12 12)"
      />
    </svg>
  );
}
